import type { AdminInspectorDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem, SectionTitle } from './InfoBlocks';
import { tokens } from '../../../design';

/**
 * Profile tab of the inspector detail drawer (AUT-125). NOT PII — the
 * inspector's bio / specializations / language / brands / coverage radius
 * / payout last4 are surfaced to all admins holding `verify_inspectors`.
 * Passport identity lives on the KycTab (architect 🟠 #4).
 *
 * Payout block surfaces `kind` (card | null) + last4 only — full
 * card PAN / WebPay tokens NEVER cross the wire (server-side
 * guarantee per `AdminInspectorPayoutDto` comment).
 */

const ChipRow = ({ values }: { values: string[] }): ReactElement => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {values.map((v) => (
      <Chip key={v} size="small" variant="outlined" label={v} />
    ))}
  </Box>
);

const PayoutValue = ({
  payout,
  t,
}: {
  payout: AdminInspectorDetailDto['payout'];
  t: ReturnType<typeof useTranslation>['t'];
}): ReactElement => {
  if (payout.kind === 'card' && payout.cardLast4) {
    return (
      <Typography variant="body2" fontFamily="monospace">
        {`${payout.cardBrand ?? ''} •••• ${payout.cardLast4}`.trim()}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {t('inspectors.profileTab.fields.payoutNone')}
    </Typography>
  );
};

export const ProfileTab = ({ inspector }: { inspector: AdminInspectorDetailDto }): ReactElement => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <SectionTitle>{t('inspectors.profileTab.sections.bio')}</SectionTitle>
        {inspector.bio ? (
          <Typography variant="body2">{inspector.bio}</Typography>
        ) : (
          <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
            {t('inspectors.profileTab.emptyBio')}
          </Typography>
        )}
      </Box>

      <Box>
        <SectionTitle>{t('inspectors.profileTab.sections.expertise')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('inspectors.profileTab.fields.experienceYears')}>
            {t('inspectors.profileTab.fields.experienceYearsValue', {
              count: inspector.experienceYears,
            })}
          </InfoItem>
          <InfoItem label={t('inspectors.profileTab.fields.languages')}>
            <ChipRow values={inspector.languages} />
          </InfoItem>
          <InfoItem label={t('inspectors.profileTab.fields.brands')} span>
            <ChipRow values={inspector.brands} />
          </InfoItem>
          <InfoItem label={t('inspectors.profileTab.fields.specializations')} span>
            <ChipRow values={inspector.specializations} />
          </InfoItem>
        </InfoGrid>
      </Box>

      <Box>
        <SectionTitle>{t('inspectors.profileTab.sections.geography')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('inspectors.profileTab.fields.maxRadiusKm')}>
            {t('inspectors.profileTab.fields.maxRadiusKmValue', { km: inspector.maxRadiusKm })}
          </InfoItem>
          <InfoItem label={t('inspectors.profileTab.fields.preferredAreas')}>
            <ChipRow values={inspector.preferredAreas} />
          </InfoItem>
        </InfoGrid>
      </Box>

      <Box>
        <SectionTitle>{t('inspectors.profileTab.sections.payout')}</SectionTitle>
        <PayoutValue payout={inspector.payout} t={t} />
      </Box>
    </Box>
  );
};
